/** One localized sentence for a camera or screen-share failure. Never the raw browser string. */

export type MediaLocale = 'nb' | 'en'

export function mediaLocaleFrom(language: string | null | undefined): MediaLocale {
  const value = (language ?? '').toLowerCase()
  if (value.startsWith('nb') || value.startsWith('nn') || value.startsWith('no')) return 'nb'
  return 'en'
}

const COPY = {
  nb: {
    cameraBusy: 'Kameraet er opptatt eller utilgjengelig. Legg ved et bilde i stedet.',
    cameraDenied: 'Kameratillatelse ble avslått. Tillat kamera i nettleseren og prøv igjen.',
    cameraUnsupported: 'Kamera støttes ikke i denne nettleseren.',
    cameraMissing: 'Fant ikke noe kamera.',
    cameraCancelled: 'Kameraforsøket ble avbrutt.',
    cameraInsecure: 'Kamera krever en sikker tilkobling (HTTPS).',
    cameraGeneric: 'Fikk ikke tilgang til kameraet. Legg ved et bilde i stedet.',
    screenDenied: 'Skjermdeling ble avslått. Tillat deling i nettleseren og prøv igjen.',
    screenUnsupported: 'Skjermdeling støttes ikke i denne nettleseren.',
    screenMissing: 'Fant ingen skjerm å dele.',
    screenTimeout: 'Skjermdeling ble tidsavbrutt. Prøv igjen.',
    screenGeneric: 'Fikk ikke tilgang til skjermdeling. Legg ved et skjermbilde i stedet.',
  },
  en: {
    cameraBusy: 'The camera is busy or unavailable. Attach an image instead.',
    cameraDenied: 'Camera permission was denied. Allow camera access in the browser and try again.',
    cameraUnsupported: 'Camera access is not supported in this browser.',
    cameraMissing: 'No camera was found.',
    cameraCancelled: 'The camera request was cancelled.',
    cameraInsecure: 'Camera access requires a secure connection (HTTPS).',
    cameraGeneric: 'The camera could not be started. Attach an image instead.',
    screenDenied: 'Screen sharing was denied. Allow sharing in the browser and try again.',
    screenUnsupported: 'Screen sharing is not supported in this browser.',
    screenMissing: 'No screen was available to share.',
    screenTimeout: 'Screen share timed out. Try again.',
    screenGeneric: 'Screen share could not be started. Attach a screenshot instead.',
  },
} as const

function errorParts(error: unknown): { name: string; message: string } {
  if (error instanceof Error) return { name: error.name || '', message: error.message || '' }
  return { name: '', message: typeof error === 'string' ? error : '' }
}

export function cameraFailureMessage(error: unknown, locale: MediaLocale): string {
  const { name, message } = errorParts(error)
  const lower = message.toLowerCase()
  const copy = COPY[locale]
  if (lower.includes('timed out')) return message
  if (
    name === 'NotReadableError' ||
    lower.includes('could not start video source') ||
    lower.includes('device in use') ||
    lower.includes('notreadable')
  ) {
    return copy.cameraBusy
  }
  if (name === 'NotAllowedError' || lower.includes('permission')) return copy.cameraDenied
  if (name === 'NotSupportedError' || lower.includes('not supported')) return copy.cameraUnsupported
  if (name === 'NotFoundError' || lower.includes('not found')) return copy.cameraMissing
  if (name === 'AbortError' || lower.includes('abort')) return copy.cameraCancelled
  if (name === 'SecurityError' || lower.includes('secure context') || lower.includes('https')) {
    return copy.cameraInsecure
  }
  return copy.cameraGeneric
}

export type ScreenShareFailure = { cancelled: true } | { cancelled: false; message: string }

/**
 * Closing the picker is NotAllowedError or AbortError. That is not a failure.
 * A timeout we started is a failure, even when the abort is what rejects the call.
 */
export function screenShareFailure(
  error: unknown,
  locale: MediaLocale,
  timedOut: boolean,
): ScreenShareFailure {
  const { name, message } = errorParts(error)
  const lower = message.toLowerCase()
  const copy = COPY[locale]
  if (timedOut || lower.includes('timed out')) {
    return { cancelled: false, message: lower.includes('timed out') ? message : copy.screenTimeout }
  }
  if (
    name === 'NotAllowedError' ||
    name === 'AbortError' ||
    lower.includes('permission denied') ||
    lower.includes('abort')
  ) {
    return { cancelled: true }
  }
  if (name === 'NotSupportedError' || lower.includes('not supported')) {
    return { cancelled: false, message: copy.screenUnsupported }
  }
  if (name === 'NotFoundError' || lower.includes('not found')) {
    return { cancelled: false, message: copy.screenMissing }
  }
  return { cancelled: false, message: copy.screenGeneric }
}
